import { useMemo, useState, useEffect, useRef } from "react";
import {
  Typography,
  Paper,
  Grid,
  TextField,
  Collapse,
  IconButton
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { DataGrid } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { ApiServices } from "../../utils/ApiService"
import { CircularProgress } from "@mui/material";

export default function ProcessorQueryUploadedFilesPage() {
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState({ file_name: "", application_id: "", from_date: "", to_date: "" });  
  const columns = [
    { field: "id", headerName: "ID", width: 90 },
    { field: "file_number", headerName: "File Number", flex: 1 },
    { field: "file_name", headerName: "Applicant Name", flex: 1 },
    { field: "application_id", headerName: "Application Id", flex: 1 },
    { field: "bank_name", headerName: "Bank", flex: 1 },
    { field: "status", headerName: "Status", width: 120 },
    { field: "initiated_by", headerName: "Initiated By", flex: 1 },
    { field: "action", headerName: "Action", flex: 1 },
    { field: "waiting_time", headerName: "Waiting Time", flex: 1 }
  ];

  const userId = useMemo(() => JSON.parse(localStorage.getItem("user"))?.id, []);

    const fetchData = async () => {
        try 
        {
            setLoading(true)
            const res = await ApiServices.get(`file/query-uploaded-files/${userId}`);
            if (res?.status === 200) {
              const apiData = res?.data;
              setRows(apiData?.length ? apiData : null);
            } else {
              console.error("Error fetching files:", res);
            }
        } 
        catch (err) 
        {
          console.error("catch error:", err);
            setRows(null);
        }
        finally
        {
          setLoading(false)
        }
      }

  const filteredRows = useMemo(() => {
    if (!Array.isArray(rows)) return [];

    return rows?.filter((row) => {
      let fileNameMatch = searchTerm?.file_name
          ? row?.file_name?.toLowerCase().includes(searchTerm?.file_name.toLowerCase())
          : true;
        
        if(!fileNameMatch){
          fileNameMatch = searchTerm?.file_name
            ? row?.file_number?.toLowerCase().includes(searchTerm?.file_name.toLowerCase())
            : true;
      }

      const appIdMatch = searchTerm.application_id
        ? row?.application_id?.toLowerCase().includes(searchTerm.application_id.toLowerCase())
        : true;

      const rowDate = row?.created_at ? new Date(row.created_at) : null;
      const fromDate = searchTerm.from_date ? new Date(searchTerm.from_date) : null;
      const toDate = searchTerm.to_date ? new Date(searchTerm.to_date) : null;

      const normalizeStart = (date) => {
            if (!date) return null;
            const d = new Date(date);
            d.setHours(0, 0, 0, 0);
            return d;
          };

          const normalizeEnd = (date) => {
            if (!date) return null;
            const d = new Date(date);
            d.setHours(23, 59, 59, 999);
            return d;
          };

          const normalizedFromDate = normalizeStart(fromDate);
          const normalizedToDate = normalizeEnd(toDate);

          const dateMatch = rowDate
            ? (!normalizedFromDate || rowDate >= normalizedFromDate) &&
              (!normalizedToDate || rowDate <= normalizedToDate)
            : true;

      return fileNameMatch && appIdMatch && dateMatch;
    });
  }, [rows, searchTerm]);
  
    const hasFetched = useRef(false);
    useEffect(() => {
      if (!hasFetched.current && userId) {
        hasFetched.current = true;
        fetchData();
      }
    }, [userId]);

  return (
    <Grid container spacing={3}>
      {/* Filters Section */}
      <Grid item xs={12} style={{ width: "100%" }}>
        <Paper elevation={3} style={{ padding: 16 }}>
          <Grid container justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Filters</Typography>
            <IconButton onClick={() => setFiltersOpen(!filtersOpen)}>
              {filtersOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Grid>

          <Collapse in={filtersOpen} timeout="auto" unmountOnExit>
              <Grid container spacing={2} style={{ marginTop: 16 }}>

                <Grid item xs={12} sm={4}>
                  <TextField 
                    label="Appl. Name or File No" 
                    variant="outlined" 
                    fullWidth size="small" 
                    value={searchTerm.file_name}
                    onChange={(e) => {
                      setSearchTerm({ file_name: e.target.value.trim(), application_id: "" });
                    }}
                    />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField 
                    label="Application Id" 
                    variant="outlined" 
                    fullWidth size="small" 
                    value={searchTerm.application_id}
                    onChange={(e) => {
                      setSearchTerm({ file_name: "", application_id: e.target.value.trim() });
                    }}                   
                    />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    label="From Date"
                    variant="outlined"
                    type="date"
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    value={searchTerm.from_date}
                    onChange={(e) =>
                      setSearchTerm((prev) => ({ ...prev, from_date: e.target.value }))
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="To Date"
                    variant="outlined"
                    type="date"
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    value={searchTerm.to_date}
                    onChange={(e) =>
                      setSearchTerm((prev) => ({ ...prev, to_date: e.target.value }))
                    }
                  />
                </Grid>
              </Grid>
            </Collapse>
        </Paper>
      </Grid>

      {/* Data Grid Section */}
      <Grid item xs={12} style={{ width: "100%" }}>
        <Paper elevation={3} style={{ padding: 16 }}>
          <Grid container justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Query Uploaded Files</Typography>
            <Typography variant="subtitle1">
              No. of Query Uploaded Files: <strong>{rows?.length || 0}</strong>
            </Typography>
          </Grid>

          <div style={{ height: 400, width: "100%", marginTop: 20 }}>
            {
              loading ? (
                <Grid container justifyContent="center" alignItems="center" style={{ height: "100%" }}>
                <CircularProgress />
                </Grid>
              ): (
                <DataGrid
                  rows={filteredRows}
                  columns={columns}
                  pageSize={5}
                  rowsPerPageOptions={[5]}
                  disableSelectionOnClick
                  autoHeight
                  onRowDoubleClick={(params) =>
                    navigate(`/processor-file-view/${params.row.id}`, {
                      state: { file: params.row }
                    })
                  }
                />
              )}
          </div>
        </Paper>
      </Grid>
    </Grid>
  );
}
